import {useCallback} from 'react';

import {Question, SurveySettings} from 'common/models/survey';
import {isEmpty} from 'lodash';
import {useSearchParams} from 'react-router-dom';

import {MatchConditions} from './useConditions';

export const getNextPage = (
    direction: 'next' | 'previous',
    currentPage: number,
    matchConditions: MatchConditions,
    survey?: SurveySettings,
) => {
    if (isEmpty(survey) || !survey?.pages) {
        return currentPage + 1;
    }
    const findNextPage = (page: number): number => {
        const upcomingPageData = survey.pages[page - 1];
        if (
            upcomingPageData?.items.every(
                (item) =>
                    !matchConditions({conditions: item.conditions, question: item.id}) ||
                    (item as Question).hidden,
            )
        ) {
            return findNextPage(direction === 'next' ? page + 1 : page - 1);
        }
        return page;
    };

    return findNextPage(direction === 'next' ? currentPage + 1 : currentPage - 1);
};

const usePages = (
    matchConditions: MatchConditions,
    survey?: SurveySettings,
): [
    number,
    (direction: 'next' | 'previous') => void,
    (pageNumber: number) => void,
    boolean,
    boolean,
] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPageNumber = Number(searchParams.get('page')) || 1;

    const setPage = useCallback(
        (pageNumber: number) => {
            setSearchParams((currentParams) => {
                currentParams.set('page', String(pageNumber));
                return currentParams;
            });
        },
        [setSearchParams],
    );

    const onChangePage = useCallback(
        (direction: 'next' | 'previous') => {
            const nextPage = getNextPage(direction, currentPageNumber, matchConditions, survey);
            setPage(nextPage);
        },
        [currentPageNumber, matchConditions, setPage, survey],
    );

    const nextPageNumber = getNextPage('next', currentPageNumber, matchConditions, survey);
    const isFirstPage = currentPageNumber === 1;
    const isLastPage =
        !survey?.pages?.length ||
        currentPageNumber === survey.pages?.length ||
        nextPageNumber > survey.pages?.length;

    return [currentPageNumber, onChangePage, setPage, isFirstPage, isLastPage];
};

export default usePages;
